import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RotateCw, Home, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Catches runtime render errors anywhere in the tree and shows a friendly,
 * accessible, brand-aligned error screen (a "500"-style fallback).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="min-h-screen flex items-center justify-center bg-background text-foreground p-6 relative overflow-x-hidden"
      >
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-[26rem] h-[26rem] rounded-full bg-destructive/10 blur-3xl animate-float" />
          <div className="absolute bottom-0 -right-24 w-[26rem] h-[26rem] rounded-full bg-primary/10 blur-3xl animate-float" style={{ animationDelay: '2.4s' }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-xl text-center"
        >
          <a
            href="/"
            aria-label="People Care Home — Home"
            className="inline-flex items-center justify-center mb-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
          >
            <img
              src="/brand/pch-logo.svg"
              alt="People Care Home"
              className="h-20 sm:h-24 w-auto object-contain dark:invert"
            />
          </a>
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-destructive" aria-hidden="true" />
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-destructive mb-3">
            Something went wrong
          </p>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
            We hit an unexpected snag
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-md mx-auto">
            An error occurred while loading this page. Reloading usually fixes it &mdash; if it keeps happening,
            our support team is ready to help.
          </p>

          <details className="mt-6 text-left text-sm bg-muted/50 border border-border rounded-lg p-4">
            <summary className="cursor-pointer font-medium text-muted-foreground select-none">
              Show technical details
            </summary>
            <pre className="mt-3 whitespace-pre-wrap break-all text-xs text-muted-foreground font-mono">
              {this.state.error?.message || String(this.state.error || 'Unknown error')}
            </pre>
          </details>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={this.handleReload} size="lg" className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/25">
              <RotateCw className="w-4 h-4" />
              Reload page
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="/">
                <Home className="w-4 h-4" />
                Go home
              </a>
            </Button>
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            Need a hand? Visit our{' '}
            <a href="/customer-support" className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">
              customer support
            </a>{' '}
            page.
          </p>
        </motion.div>
      </div>
    );
  }
}